import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { messagesService } from '../services/api';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react/dist/iconify.js';

const MessagesPage = () => {
  const location = useLocation();
  const isMessagesPage = location.pathname === '/messages';
  const [activeChannel, setActiveChannel] = useState('whatsapp');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const messagesContainerRef = useRef(null);
  const [lastMessageId, setLastMessageId] = useState(null);
  const [lastConversationUpdate, setLastConversationUpdate] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});

  const channels = [
    { 
      key: 'whatsapp', 
      label: 'WhatsApp', 
      icon: 'solar:chat-round-dots-bold', 
      color: '#25D366', 
      bgColor: '#DCF8C6',
      textColor: '#000',
      enabled: true 
    },
    { 
      key: 'instagram', 
      label: 'Instagram', 
      icon: 'solar:instagram-bold', 
      color: '#E4405F', 
      bgColor: '#FCE4EC',
      textColor: '#000',
      enabled: false 
    },
    { 
      key: 'facebook', 
      label: 'Facebook', 
      icon: 'solar:facebook-bold', 
      color: '#1877F2', 
      bgColor: '#E3F2FD',
      textColor: '#000',
      enabled: false 
    },
    { 
      key: 'mail', 
      label: 'E-posta', 
      icon: 'solar:letter-bold', 
      color: '#FF9800', 
      bgColor: '#FFF3E0',
      textColor: '#000',
      enabled: false 
    },
    { 
      key: 'randuapp', 
      label: 'RanduApp', 
      icon: 'solar:chat-round-call-bold', 
      color: '#7B2CBF', 
      bgColor: '#F3E5F5',
      textColor: '#000',
      enabled: false 
    }
  ];

  // İlk yükleme
  useEffect(() => {
    loadConversations();
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [activeChannel]);

  // Seçili konuşma değiştiğinde mesajları yükle
  useEffect(() => {
    if (selectedConversation) {
      // Reset pagination ve messages
      setPagination({ page: 1, limit: 50, total: 0 });
      setMessages([]);
      setHasMoreMessages(true);
      isInitialLoadRef.current = true;
      setLastMessageId(null); // Reset last message ID
      // loadMessages'ı çağır
      const contactIdentifier = selectedConversation.contact_identifier || selectedConversation.phone_number;
      loadMessages(contactIdentifier, false, 1, false);
    }
  }, [selectedConversation, activeChannel]);

  // Mesajlar değiştiğinde scroll et - Sadece yeni mesaj geldiğinde veya ilk yüklemede
  const previousMessagesLengthRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  
  useEffect(() => {
    // İlk yükleme veya yeni mesaj eklendiğinde scroll et
    if (isInitialLoadRef.current || messages.length > previousMessagesLengthRef.current) {
      // Kısa bir gecikme ile scroll et (DOM güncellemesi için)
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      isInitialLoadRef.current = false;
    }
    
    // Son mesaj ID'sini güncelle
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      setLastMessageId(lastMsg.id);
    }
    
    previousMessagesLengthRef.current = messages.length;
  }, [messages]);

  // Real-time polling: Her 1 saniyede bir mesajları kontrol et (anlık güncelleme)
  useEffect(() => {
    if (selectedConversation && activeChannel === 'whatsapp') {
      // Polling'i başlat - sessiz mod (loading state yok)
      pollingIntervalRef.current = setInterval(() => {
        checkForNewMessages();
      }, 1000); // 1 saniyede bir kontrol et (anlık güncelleme)

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    } else {
      // Konuşma seçili değilse polling'i durdur
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    }
  }, [selectedConversation, activeChannel]);

  // Konuşmaları da periyodik olarak güncelle (her 10 saniyede bir, sessiz)
  useEffect(() => {
    const conversationInterval = setInterval(() => {
      if (activeChannel === 'whatsapp') {
        // Sessiz güncelleme - loading state yok
        loadConversations(true); // silent = true
      }
    }, 10000); // 10 saniyede bir konuşmaları güncelle (daha az sıklık)

    return () => {
      clearInterval(conversationInterval);
    };
  }, [activeChannel]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const params = {
        channel: activeChannel,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Arama varsa ekle
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const response = await messagesService.getMessages(params);

      if (response.success) {
        // Konuşmaları last_message_at'e göre sırala (en yeni en üstte)
        const sortedConversations = (response.data || []).sort((a, b) => {
          const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
          const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
          return dateB - dateA; // Azalan sıralama (en yeni en üstte)
        });
        setConversations(sortedConversations);
        
        // Toplam okunmamış mesaj sayısını hesapla (kanal başlıkları için)
        const totalUnread = sortedConversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setUnreadCounts(prev => ({ ...prev, [activeChannel]: totalUnread }));
        
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        if (!silent) {
          toast.error(response.error || 'Konuşmalar yüklenemedi');
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      if (!silent) {
        toast.error('Konuşmalar yüklenirken bir hata oluştu');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };
  
  // Arama değiştiğinde konuşmaları yeniden yükle
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeChannel === 'whatsapp') {
        loadConversations(true);
      }
    }, 500); // Debounce 500ms
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const loadMessages = async (contactIdentifier, silent = false, page = 1, append = false) => {
    try {
      const response = await messagesService.getConversation(contactIdentifier, {
        channel: activeChannel,
        page: page,
        limit: 50
      });

      if (response.success) {
        const newMessages = response.data.messages || [];
        const total = response.data.pagination?.total || 0;
        
        // Daha fazla mesaj var mı?
        setHasMoreMessages(newMessages.length === 50 && (append ? messages.length + newMessages.length < total : newMessages.length < total));
        
        if (append) {
          // Eski mesajları ekle (başa ekle)
          setMessages(prev => [...newMessages, ...prev]);
        } else {
          // Yeni mesajlar (ilk yükleme veya refresh)
          // Yeni mesaj var mı kontrol et
          if (lastMessageId && newMessages.length > 0) {
            const lastNewMsg = newMessages[newMessages.length - 1];
            if (lastNewMsg.id > lastMessageId) {
              // Yeni mesaj var, bildirim göster (sadece gelen mesajlar için)
              const newIncomingMessages = newMessages.filter(msg => 
                msg.id > lastMessageId && msg.message_type === 'incoming'
              );
              if (newIncomingMessages.length > 0 && !silent) {
                toast.info(`${newIncomingMessages.length} yeni mesaj geldi`);
              }
            }
          }
          
          setMessages(newMessages);
          isInitialLoadRef.current = true; // İlk yükleme olarak işaretle
        }
        
        setPagination(prev => ({
          ...prev,
          page: page,
          total: total
        }));
        
        // User bilgisini güncelle ama selectedConversation'ı değiştirme (sonsuz döngüyü önle)
        // User bilgisi zaten selectedConversation'da var, sadece güncelle
        if (response.data.user && selectedConversation) {
          // Sadece user bilgisi yoksa veya farklıysa güncelle
          if (!selectedConversation.user || selectedConversation.user.id !== response.data.user.id) {
            setSelectedConversation(prev => ({
              ...prev,
              user: response.data.user
            }));
          }
        }
      } else {
        if (!silent) {
          toast.error(response.error || 'Mesajlar yüklenemedi');
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      if (!silent) {
        toast.error('Mesajlar yüklenirken bir hata oluştu');
      }
    } finally {
      setLoadingMore(false);
    }
  };
  
  // Scroll yukarı çekildiğinde eski mesajları yükle (Lazy Loading)
  const handleScroll = (e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    
    // Yukarı çekildiğinde (scrollTop < 100) ve daha fazla mesaj varsa
    if (scrollTop < 100 && hasMoreMessages && !loadingMore) {
      setLoadingMore(true);
      const nextPage = pagination.page + 1;
      const contactIdentifier = selectedConversation?.contact_identifier || selectedConversation?.phone_number;
      if (contactIdentifier) {
        // Scroll pozisyonunu koru
        const previousScrollHeight = container.scrollHeight;
        loadMessages(contactIdentifier, true, nextPage, true).then(() => {
          // Scroll pozisyonunu koru (yeni mesajlar eklendiğinde)
          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - previousScrollHeight;
          }, 50);
        });
      }
    }
  };

  // Yeni mesajları kontrol et (polling için) - Tüm yeni mesajları al ve ekle
  const checkForNewMessages = async () => {
    if (!selectedConversation) return;

    const contactIdentifier = selectedConversation.contact_identifier || selectedConversation.phone_number;
    if (!contactIdentifier || !lastMessageId) return; // lastMessageId yoksa henüz mesaj yüklenmemiş, bekle

    try {
      // Son 50 mesajı al ve yeni olanları kontrol et
      const response = await messagesService.getConversation(contactIdentifier, {
        channel: activeChannel,
        page: 1,
        limit: 50
      });

      if (response.success) {
        const allMessages = response.data.messages || [];
        
        // Yeni mesaj var mı kontrol et
        if (allMessages.length > 0) {
          // Mevcut mesajlarla karşılaştır, sadece yeni olanları ekle
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = allMessages.filter(m => m.id > lastMessageId && !existingIds.has(m.id));
            
            if (newMsgs.length > 0) {
              // Yeni mesajlar var, ekle (sıralı olarak)
              const updatedMessages = [...prev, ...newMsgs].sort((a, b) => {
                const dateA = new Date(a.created_at).getTime();
                const dateB = new Date(b.created_at).getTime();
                return dateA - dateB; // Artan sıralama (eski -> yeni)
              });
              
              const newLastMessageId = updatedMessages[updatedMessages.length - 1]?.id;
              if (newLastMessageId) {
                setLastMessageId(newLastMessageId);
              }
              
              // Sadece gelen mesajlar için kontrol et
              const newIncomingMessages = newMsgs.filter(msg => msg.message_type === 'incoming');
              if (newIncomingMessages.length > 0) {
                // Konuşma listesini güncelle (en üste çıkması için)
                loadConversations(true);
                
                // Bildirim göster (sadece mesajlar sayfasında değilse veya farklı kanaldaysa)
                if (!isMessagesPage || activeChannel !== 'whatsapp') {
                  const channelName = channels.find(c => c.key === activeChannel)?.label || activeChannel;
                  toast.info(`${channelName}: ${newIncomingMessages.length} yeni mesaj`, {
                    onClick: () => {
                      if (!isMessagesPage) {
                        window.location.href = '/messages';
                      } else {
                        setActiveChannel(activeChannel);
                      }
                    }
                  });
                }
              }
              
              return updatedMessages;
            }
            
            return prev; // Yeni mesaj yok, mevcut listeyi koru
          });
        }
      }
    } catch (error) {
      // Sessizce hata yut, polling devam etsin
      console.error('Error checking new messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      setSending(true);
      const contactIdentifier = selectedConversation.contact_identifier || selectedConversation.phone_number;
      const messageData = {
        contact_identifier: contactIdentifier,
        message: newMessage,
        channel: activeChannel
      };

      // Channel-specific fields
      if (activeChannel === 'whatsapp') {
        messageData.phone_number = contactIdentifier;
      } else if (activeChannel === 'mail') {
        messageData.email = contactIdentifier;
      } else if (activeChannel === 'instagram') {
        messageData.username = contactIdentifier;
      }

      const response = await messagesService.sendMessage(messageData);

      if (response.success) {
        setNewMessage('');
        
        // Optimistik güncelleme: Mesajı hemen UI'a ekle
        const tempMessage = {
          id: `temp_${Date.now()}`,
          message_type: 'outgoing',
          message_body: newMessage,
          status: 'sending',
          created_at: new Date().toISOString(),
          user_id: null,
          user_name: null
        };
        setMessages(prev => [...prev, tempMessage]);
        scrollToBottom();
        
        // Reload messages (gerçek mesajı al)
        await loadMessages(contactIdentifier, true);
        // Reload conversations to update last message (en üste çıkması için)
        await loadConversations(true);
        toast.success('Mesaj gönderildi');
      } else {
        toast.error(response.error || 'Mesaj gönderilemedi');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Mesaj gönderilirken bir hata oluştu');
    } finally {
      setSending(false);
    }
  };

  // Tarih formatla - Backend'den CONVERT_TZ ile Türkiye saati geliyor
  const formatTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Date string'i parse et (backend'den Türkiye saati olarak geliyor)
      // MySQL datetime formatı: 'YYYY-MM-DD HH:MM:SS' (zaten +03:00 timezone'unda)
      // MySQL'den gelen datetime'ı Türkiye saati olarak parse et
      let date;
      if (typeof dateString === 'string' && dateString.includes(' ')) {
        // MySQL datetime formatını parse et - Türkiye saati olarak
        // Örnek: "2025-12-01 14:35:41" -> Türkiye saati 14:35:41 (UTC+3)
        const [datePart, timePart] = dateString.split(' ');
        const [year, month, day] = datePart.split('-');
        const [hour, minute, second] = timePart.split(':');
        
        // MySQL'den gelen datetime zaten +03:00 timezone'unda
        // JavaScript Date objesi UTC olarak çalışır, bu yüzden UTC'ye çevirirken 3 saat geri almalıyız
        // Örnek: "2025-12-01 14:35:41" (Türkiye saati) = "2025-12-01 11:35:41" (UTC)
        date = new Date(Date.UTC(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour) - 3, // UTC'ye çevir (Türkiye saati - 3 saat = UTC)
          parseInt(minute),
          parseInt(second || 0)
        ));
      } else {
        date = new Date(dateString);
      }
      
      // Eğer tarih geçersizse
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }
      
      // Şu anki zamanı al (UTC)
      const now = new Date();
      
      // Farkı hesapla (milliseconds)
      const diff = now.getTime() - date.getTime();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (minutes < 1) return 'Şimdi';
      if (minutes < 60) return `${minutes} dk önce`;
      if (hours < 24) return `${hours} sa önce`;
      if (days < 7) return `${days} gün önce`;
      
      // Türkiye saatine göre formatla (Europe/Istanbul timezone)
      return date.toLocaleDateString('tr-TR', { 
        day: 'numeric', 
        month: 'short', 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'Europe/Istanbul'
      });
    } catch (error) {
      console.error('Date format error:', error, dateString);
      return '';
    }
  };

  // Mesaj durumu ikonu - WhatsApp mantığı: tek tik (sent/delivered), çift tik (read)
  const getMessageStatusIcon = (status, messageType) => {
    if (messageType !== 'outgoing') return null;
    
    if (status === 'failed' || status === 'undelivered') {
      return <Icon icon="solar:close-circle-bold" style={{ fontSize: '14px', color: '#f44336' }} />;
    } else if (status === 'read') {
      // Okundu - çift mavi tik
      return <Icon icon="solar:double-check-read-bold" style={{ fontSize: '14px', color: '#25D366' }} />;
    } else if (status === 'delivered' || status === 'received') {
      // İletildi - tek mavi tik
      return <Icon icon="solar:check-read-bold" style={{ fontSize: '14px', color: '#25D366' }} />;
    } else if (status === 'sent' || status === 'sending') {
      // Gönderildi - tek gri tik
      return <Icon icon="solar:check-circle-bold" style={{ fontSize: '14px', color: '#999999' }} />;
    }
    return <Icon icon="solar:time-bold" style={{ fontSize: '14px', color: '#999999' }} />;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // +90XXXXXXXXXX formatını +90 XXX XXX XX XX formatına çevir
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('90') && cleaned.length === 12) {
      return `+${cleaned.substring(0, 2)} ${cleaned.substring(2, 5)} ${cleaned.substring(5, 8)} ${cleaned.substring(8, 10)} ${cleaned.substring(10)}`;
    }
    return phone;
  };

  return (
    <MasterLayout>
      <div className="dashboard-main-content">
        <div className="container-fluid">
          <Breadcrumb
            items={[
              { label: 'Dashboard', link: '/dashboard' },
              { label: 'Mesajlar', link: '#' }
            ]}
          />

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <Icon icon="solar:chat-round-dots-bold" className="me-2" />
                    Mesajlar
                  </h5>
                </div>
                <div className="card-body p-0">
                  {/* Channel Tabs - UX/UI Standartları */}
                  <div className="border-bottom" style={{ backgroundColor: '#FFFFFF' }}>
                    <ul className="nav nav-tabs nav-tabs-custom" role="tablist" style={{ borderBottom: 'none', marginBottom: 0 }}>
                      {channels.map((channel) => {
                        const unreadCount = unreadCounts[channel.key] || 0;
                        return (
                          <li className="nav-item" key={channel.key}>
                            <button
                              className={`nav-link ${activeChannel === channel.key ? 'active' : ''} ${!channel.enabled ? 'disabled' : ''}`}
                              onClick={() => channel.enabled && setActiveChannel(channel.key)}
                              disabled={!channel.enabled}
                              type="button"
                              style={{
                                backgroundColor: activeChannel === channel.key ? '#FFFFFF' : 'transparent',
                                color: activeChannel === channel.key ? channel.color : '#667781',
                                borderBottom: activeChannel === channel.key ? `3px solid ${channel.color}` : '3px solid transparent',
                                fontWeight: activeChannel === channel.key ? '600' : '400',
                                padding: '12px 16px',
                                borderTop: 'none',
                                borderLeft: 'none',
                                borderRight: 'none',
                                position: 'relative',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <Icon icon={channel.icon} className="me-2" style={{ color: activeChannel === channel.key ? channel.color : '#667781' }} />
                              {channel.label}
                              {unreadCount > 0 && channel.enabled && (
                                <span 
                                  style={{
                                    backgroundColor: channel.color,
                                    color: '#FFFFFF',
                                    borderRadius: '10px',
                                    padding: '2px 6px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    marginLeft: '6px',
                                    minWidth: '18px',
                                    textAlign: 'center',
                                    display: 'inline-block'
                                  }}
                                >
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                              )}
                              {!channel.enabled && (
                                <span className="badge bg-secondary ms-2" style={{ fontSize: '10px' }}>Yakında</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="row g-0" style={{ height: 'calc(100vh - 280px)', minHeight: '500px', display: 'flex', overflow: 'hidden', flexWrap: 'nowrap' }}>
                    {/* Conversations List - WhatsApp Style */}
                    <div 
                      className="col-md-4 d-flex flex-column" 
                      style={{ 
                        backgroundColor: '#FFFFFF',
                        borderRight: '1px solid #e0e0e0',
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {/* Conversations Header - Arama ile */}
                      <div 
                        className="border-bottom"
                        style={{ 
                          backgroundColor: activeChannel === 'whatsapp' ? '#075E54' : 
                                        activeChannel === 'instagram' ? '#E4405F' :
                                        activeChannel === 'facebook' ? '#1877F2' :
                                        activeChannel === 'mail' ? '#FF9800' :
                                        activeChannel === 'randuapp' ? '#7B2CBF' : '#075E54',
                          color: '#FFFFFF',
                          flexShrink: 0,
                          padding: '8px'
                        }}
                      >
                        {activeChannel === 'whatsapp' && (
                          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                              <Icon 
                                icon="solar:magnifer-bold" 
                                style={{ 
                                  position: 'absolute', 
                                  left: '12px', 
                                  top: '50%', 
                                  transform: 'translateY(-50%)',
                                  color: 'rgba(255,255,255,0.7)',
                                  fontSize: '18px',
                                  zIndex: 1
                                }} 
                              />
                              <input
                                type="text"
                                placeholder="Ara (isim, telefon, mesaj)..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px 8px 40px',
                                  borderRadius: '20px',
                                  border: 'none',
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  color: '#FFFFFF',
                                  fontSize: '14px',
                                  outline: 'none'
                                }}
                                onFocus={(e) => {
                                  e.target.style.backgroundColor = 'rgba(255,255,255,0.3)';
                                  e.target.style.color = '#FFFFFF';
                                }}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                                  e.target.style.color = '#FFFFFF';
                                }}
                              />
                              <style>{`
                                input[type="text"]::placeholder {
                                  color: rgba(255, 255, 255, 0.7) !important;
                                }
                                input[type="text"]::-webkit-input-placeholder {
                                  color: rgba(255, 255, 255, 0.7) !important;
                                }
                                input[type="text"]::-moz-placeholder {
                                  color: rgba(255, 255, 255, 0.7) !important;
                                }
                                input[type="text"]:-ms-input-placeholder {
                                  color: rgba(255, 255, 255, 0.7) !important;
                                }
                              `}</style>
                              {searchQuery && (
                                <button
                                  onClick={() => setSearchQuery('')}
                                  style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: 'rgba(255,255,255,0.7)',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    zIndex: 1
                                  }}
                                >
                                  <Icon icon="solar:close-circle-bold" style={{ fontSize: '18px' }} />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {activeChannel !== 'whatsapp' && (
                          <h6 className="mb-0 p-2" style={{ color: '#FFFFFF', fontWeight: '500' }}>Konuşmalar</h6>
                        )}
                      </div>
                      
                      {/* Conversations Scroll Area - Sadece içerik scroll */}
                      <div style={{ 
                        flex: 1, 
                        overflowY: 'auto', 
                        overflowX: 'hidden',
                        minHeight: 0,
                        maxHeight: '100%',
                        WebkitOverflowScrolling: 'touch' // iOS smooth scroll
                      }}>
                        {loading ? (
                          <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Yükleniyor...</span>
                            </div>
                          </div>
                        ) : conversations.length === 0 ? (
                          <div className="text-center py-5 text-muted">
                            <Icon icon="solar:chat-round-dots-bold" style={{ fontSize: '3rem', opacity: 0.3 }} />
                            <p className="mt-3">Henüz konuşma yok</p>
                          </div>
                        ) : (
                          <div>
                            {conversations.map((conv) => {
                              const contactId = conv.contact_identifier || conv.phone_number || conv.email || conv.username;
                              const displayName = conv.user_name || 
                                (activeChannel === 'whatsapp' ? formatPhoneNumber(conv.phone_number || contactId) : 
                                 activeChannel === 'mail' ? (conv.email || contactId) :
                                 activeChannel === 'instagram' ? (conv.username || contactId) : contactId);
                              const isSelected = selectedConversation?.contact_identifier === contactId || 
                                                selectedConversation?.phone_number === contactId;
                              
                              return (
                                <div
                                  key={contactId}
                                  onClick={() => setSelectedConversation({ ...conv, contact_identifier: contactId })}
                                  style={{
                                    padding: '12px 16px',
                                    borderBottom: '1px solid #f0f0f0',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0f0f0' : '#FFFFFF',
                                    transition: 'background-color 0.2s',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f5f5f5';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#FFFFFF';
                                  }}
                                >
                                  <div style={{ flexShrink: 0, marginRight: '12px' }}>
                                    <div 
                                      className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                      style={{ 
                                        width: '49px',
                                        height: '49px',
                                        backgroundColor: isSelected 
                                          ? (activeChannel === 'whatsapp' ? '#25D366' : channels.find(c => c.key === activeChannel)?.color || '#007bff')
                                          : '#DFE5E7',
                                        color: isSelected ? '#FFFFFF' : '#54656F',
                                        fontSize: '18px',
                                        fontWeight: '500'
                                      }}
                                    >
                                      {conv.user_name ? (
                                        conv.user_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                                      ) : (
                                        <Icon icon="solar:user-bold" style={{ fontSize: '24px' }} />
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '8px' }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', flexWrap: 'wrap' }}>
                                          <h6 className="mb-0" style={{ fontSize: '16px', fontWeight: '400', color: '#000000', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>
                                            {displayName}
                                          </h6>
                                          {/* Kullanıcı etiketleri - Badges olarak */}
                                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            {(() => {
                                              const badges = [];
                                              
                                              // Debug: Frontend'de gelen veriyi logla
                                              if (process.env.NODE_ENV === 'development') {
                                                console.log('🔍 Frontend conversation data:', {
                                                  contact_identifier: conv.contact_identifier,
                                                  user_roles: conv.user_roles,
                                                  user_roles_type: typeof conv.user_roles,
                                                  partner_name: conv.partner_name,
                                                  team_member_name: conv.team_member_name
                                                });
                                              }
                                              
                                              // user_roles kontrolü - Her rol ayrı badge
                                              const userRoles = conv.user_roles;
                                              if (userRoles && typeof userRoles === 'string' && userRoles.trim() && userRoles.trim() !== 'null' && userRoles.trim() !== 'undefined') {
                                                const roles = userRoles.split(',').map(r => r.trim()).filter(r => r && r !== 'null' && r !== 'undefined');
                                                roles.forEach(role => {
                                                  let bgColor = '#E3F2FD';
                                                  let textColor = '#1976D2';
                                                  
                                                  if (role === 'Owner') {
                                                    bgColor = '#FFF3E0';
                                                    textColor = '#F57C00';
                                                  } else if (role === 'Personel') {
                                                    bgColor = '#E3F2FD';
                                                    textColor = '#1976D2';
                                                  } else if (role === 'Müşteri') {
                                                    bgColor = '#E8F5E9';
                                                    textColor = '#388E3C';
                                                  }
                                                  
                                                  badges.push(
                                                    <span 
                                                      key={`role-${role}`}
                                                      style={{ 
                                                        fontSize: '10px', 
                                                        padding: '2px 6px', 
                                                        borderRadius: '10px', 
                                                        backgroundColor: bgColor, 
                                                        color: textColor, 
                                                        fontWeight: '500', 
                                                        whiteSpace: 'nowrap',
                                                        display: 'inline-block'
                                                      }}
                                                    >
                                                      {role}
                                                    </span>
                                                  );
                                                });
                                              }
                                              
                                              // Partner kontrolü
                                              const partnerName = conv.partner_name;
                                              if (partnerName && typeof partnerName === 'string' && partnerName.trim() && partnerName.trim() !== 'null' && partnerName.trim() !== 'undefined') {
                                                badges.push(
                                                  <span 
                                                    key="partner"
                                                    style={{ 
                                                      fontSize: '10px', 
                                                      padding: '2px 6px', 
                                                      borderRadius: '10px', 
                                                      backgroundColor: '#E8F5E9', 
                                                      color: '#388E3C', 
                                                      fontWeight: '500', 
                                                      whiteSpace: 'nowrap',
                                                      display: 'inline-block'
                                                    }}
                                                  >
                                                    Partner
                                                  </span>
                                                );
                                              }
                                              
                                              // Team member kontrolü
                                              const teamMemberName = conv.team_member_name;
                                              if (teamMemberName && typeof teamMemberName === 'string' && teamMemberName.trim() && teamMemberName.trim() !== 'null' && teamMemberName.trim() !== 'undefined') {
                                                badges.push(
                                                  <span 
                                                    key="team-member"
                                                    style={{ 
                                                      fontSize: '10px', 
                                                      padding: '2px 6px', 
                                                      borderRadius: '10px', 
                                                      backgroundColor: '#FFF3E0', 
                                                      color: '#F57C00', 
                                                      fontWeight: '500', 
                                                      whiteSpace: 'nowrap',
                                                      display: 'inline-block'
                                                    }}
                                                  >
                                                    Partner Ekip Üyesi
                                                  </span>
                                                );
                                              }
                                              
                                              return badges.length > 0 ? badges : null;
                                            })()}
                                          </div>
                                        </div>
                                        {/* Telefon numarası veya email */}
                                        {(conv.user_phone || conv.phone_number || conv.partner_phone || conv.team_member_phone) && (
                                          <small style={{ fontSize: '12px', color: '#667781', display: 'block' }}>
                                            {formatPhoneNumber(conv.user_phone || conv.phone_number || conv.partner_phone || conv.team_member_phone)}
                                          </small>
                                        )}
                                      </div>
                                      <small style={{ fontSize: '12px', color: '#667781', flexShrink: 0 }}>
                                        {formatTime(conv.last_message_at)}
                                      </small>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <p 
                                        className="mb-0" 
                                        style={{ 
                                          fontSize: '14px', 
                                          color: '#667781', 
                                          overflow: 'hidden', 
                                          textOverflow: 'ellipsis', 
                                          whiteSpace: 'nowrap',
                                          margin: 0
                                        }}
                                      >
                                        {conv.last_message || 'Mesaj yok'}
                                      </p>
                                      {conv.unread_count > 0 && (
                                        <span 
                                          style={{
                                            backgroundColor: activeChannel === 'whatsapp' ? '#25D366' : channels.find(c => c.key === activeChannel)?.color || '#007bff',
                                            color: '#FFFFFF',
                                            borderRadius: '10px',
                                            padding: '2px 6px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            minWidth: '20px',
                                            textAlign: 'center',
                                            flexShrink: 0,
                                            marginLeft: '8px'
                                          }}
                                        >
                                          {conv.unread_count}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Messages Area - WhatsApp Style */}
                    <div className="col-md-8 d-flex flex-column" style={{ backgroundColor: '#ECE5DD', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {selectedConversation ? (
                        <>
                          {/* Conversation Header - WhatsApp Style */}
                          <div 
                            className="d-flex align-items-center justify-content-between p-3"
                            style={{ 
                              backgroundColor: activeChannel === 'whatsapp' ? '#075E54' : 
                                            activeChannel === 'instagram' ? '#E4405F' :
                                            activeChannel === 'facebook' ? '#1877F2' :
                                            activeChannel === 'mail' ? '#FF9800' :
                                            activeChannel === 'randuapp' ? '#7B2CBF' : '#075E54',
                              color: '#FFFFFF',
                              flexShrink: 0,
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div className="d-flex align-items-center">
                              <div 
                                className="rounded-circle d-flex align-items-center justify-content-center text-white me-3"
                                style={{ 
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: 'rgba(255,255,255,0.2)',
                                  fontSize: '16px'
                                }}
                              >
                                {selectedConversation.user_name ? (
                                  selectedConversation.user_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                                ) : (
                                  <Icon icon="solar:user-bold" />
                                )}
                              </div>
                              <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <h6 className="mb-0" style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '400' }}>
                                    {selectedConversation.user_name || 
                                     (activeChannel === 'whatsapp' ? formatPhoneNumber(selectedConversation.phone_number || selectedConversation.contact_identifier) :
                                      activeChannel === 'mail' ? (selectedConversation.email || selectedConversation.contact_identifier) :
                                      activeChannel === 'instagram' ? (selectedConversation.username || selectedConversation.contact_identifier) :
                                      selectedConversation.contact_identifier)}
                                  </h6>
                                  {/* Kullanıcı etiketleri - Header'da - Badges olarak */}
                                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {(() => {
                                      const badges = [];
                                      
                                      // user_roles kontrolü - Her rol ayrı badge
                                      const userRoles = selectedConversation.user_roles;
                                      if (userRoles && typeof userRoles === 'string' && userRoles.trim() && userRoles.trim() !== 'null' && userRoles.trim() !== 'undefined') {
                                        const roles = userRoles.split(',').map(r => r.trim()).filter(r => r && r !== 'null' && r !== 'undefined');
                                        roles.forEach(role => {
                                          let bgColor = 'rgba(255,255,255,0.3)';
                                          let textColor = '#FFFFFF';
                                          
                                          if (role === 'Owner') {
                                            bgColor = 'rgba(255,152,0,0.3)';
                                            textColor = '#FF9800';
                                          } else if (role === 'Personel') {
                                            bgColor = 'rgba(33,150,243,0.3)';
                                            textColor = '#2196F3';
                                          } else if (role === 'Müşteri') {
                                            bgColor = 'rgba(76,175,80,0.3)';
                                            textColor = '#4CAF50';
                                          }
                                          
                                          badges.push(
                                            <span 
                                              key={`role-${role}`}
                                              style={{ 
                                                fontSize: '10px', 
                                                padding: '2px 6px', 
                                                borderRadius: '10px', 
                                                backgroundColor: bgColor, 
                                                color: textColor, 
                                                fontWeight: '500', 
                                                whiteSpace: 'nowrap',
                                                display: 'inline-block'
                                              }}
                                            >
                                              {role}
                                            </span>
                                          );
                                        });
                                      }
                                      
                                      // Partner kontrolü
                                      const partnerName = selectedConversation.partner_name;
                                      if (partnerName && typeof partnerName === 'string' && partnerName.trim() && partnerName.trim() !== 'null' && partnerName.trim() !== 'undefined') {
                                        badges.push(
                                          <span 
                                            key="partner"
                                            style={{ 
                                              fontSize: '10px', 
                                              padding: '2px 6px', 
                                              borderRadius: '10px', 
                                              backgroundColor: 'rgba(76,175,80,0.3)', 
                                              color: '#4CAF50', 
                                              fontWeight: '500', 
                                              whiteSpace: 'nowrap',
                                              display: 'inline-block'
                                            }}
                                          >
                                            Partner
                                          </span>
                                        );
                                      }
                                      
                                      // Team member kontrolü
                                      const teamMemberName = selectedConversation.team_member_name;
                                      if (teamMemberName && typeof teamMemberName === 'string' && teamMemberName.trim() && teamMemberName.trim() !== 'null' && teamMemberName.trim() !== 'undefined') {
                                        badges.push(
                                          <span 
                                            key="team-member"
                                            style={{ 
                                              fontSize: '10px', 
                                              padding: '2px 6px', 
                                              borderRadius: '10px', 
                                              backgroundColor: 'rgba(255,152,0,0.3)', 
                                              color: '#FF9800', 
                                              fontWeight: '500', 
                                              whiteSpace: 'nowrap',
                                              display: 'inline-block'
                                            }}
                                          >
                                            Partner Ekip Üyesi
                                          </span>
                                        );
                                      }
                                      
                                      return badges.length > 0 ? badges : null;
                                    })()}
                                  </div>
                                </div>
                                <small style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                                  {activeChannel === 'whatsapp' && selectedConversation.phone_number && (
                                    <span>{formatPhoneNumber(selectedConversation.phone_number)}</span>
                                  )}
                                  {activeChannel === 'mail' && (selectedConversation.user_email || selectedConversation.email) && (
                                    <span>{selectedConversation.user_email || selectedConversation.email}</span>
                                  )}
                                  {activeChannel === 'instagram' && selectedConversation.username && (
                                    <span>@{selectedConversation.username}</span>
                                  )}
                                  {!selectedConversation.user_name && !selectedConversation.phone_number && !selectedConversation.email && !selectedConversation.username && (
                                    <span>{selectedConversation.contact_identifier}</span>
                                  )}
                                </small>
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm"
                                onClick={() => {
                                  // Archive conversation
                                  // TODO: Implement archive
                                }}
                                title="Arşivle"
                                style={{ 
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  color: '#FFFFFF',
                                  padding: '8px'
                                }}
                              >
                                <Icon icon="solar:archive-bold" />
                              </button>
                            </div>
                          </div>

                          {/* Messages List - WhatsApp Style - Sadece içerik scroll */}
                          <div 
                            ref={messagesContainerRef}
                            onScroll={handleScroll}
                            style={{ 
                              flex: 1,
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              padding: '16px',
                              backgroundColor: activeChannel === 'whatsapp' ? '#ECE5DD' : 
                                            activeChannel === 'instagram' ? '#FCE4EC' :
                                            activeChannel === 'facebook' ? '#E3F2FD' :
                                            activeChannel === 'mail' ? '#FFF3E0' :
                                            activeChannel === 'randuapp' ? '#F3E5F5' : '#ECE5DD',
                              backgroundImage: activeChannel === 'whatsapp' ? 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'grid\' width=\'20\' height=\'20\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M 20 0 L 0 0 0 20\' fill=\'none\' stroke=\'%23e0e0e0\' stroke-width=\'0.5\' opacity=\'0.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23grid)\'/%3E%3C/svg%3E")' : 'none',
                              backgroundRepeat: 'repeat',
                              minHeight: 0,
                              maxHeight: '100%',
                              WebkitOverflowScrolling: 'touch' // iOS smooth scroll
                            }}
                          >
                            {loadingMore && (
                              <div className="text-center py-2">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                  <span className="visually-hidden">Yükleniyor...</span>
                                </div>
                              </div>
                            )}
                            {messages.map((msg) => {
                              const channelConfig = channels.find(c => c.key === activeChannel);
                              const isOutgoing = msg.message_type === 'outgoing';
                              
                              return (
                                <div
                                  key={msg.id}
                                  className={`d-flex mb-1 ${isOutgoing ? 'justify-content-end' : 'justify-content-start'}`}
                                  style={{ marginBottom: '4px' }}
                                >
                                  <div
                                    style={{ 
                                      maxWidth: '65%',
                                      minWidth: '100px',
                                      padding: '6px 7px 8px 9px',
                                      borderRadius: isOutgoing ? '7.5px 0 7.5px 7.5px' : '0 7.5px 7.5px 7.5px',
                                      backgroundColor: isOutgoing 
                                        ? (activeChannel === 'whatsapp' ? '#DCF8C6' : channelConfig?.bgColor || '#007bff')
                                        : '#FFFFFF',
                                      color: isOutgoing ? (channelConfig?.textColor || '#000') : '#303030',
                                      boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                                      position: 'relative'
                                    }}
                                  >
                                    {/* WhatsApp için telefon numarası göster (gelen mesajlarda) */}
                                    {!isOutgoing && activeChannel === 'whatsapp' && msg.phone_number && (
                                      <div style={{ fontSize: '13px', fontWeight: '500', color: '#667781', marginBottom: '2px' }}>
                                        {formatPhoneNumber(msg.phone_number)}
                                      </div>
                                    )}
                                    <div style={{ fontSize: '14.2px', lineHeight: '19px', wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                                      {msg.message_body}
                                    </div>
                                    <div 
                                      className="d-flex align-items-center justify-content-end"
                                      style={{ 
                                        marginTop: '2px',
                                        fontSize: '11px',
                                        color: isOutgoing ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.45)',
                                        gap: '4px'
                                      }}
                                    >
                                      <span>{formatTime(msg.created_at)}</span>
                                      {isOutgoing && (
                                        <span style={{ marginLeft: '4px', display: 'flex', alignItems: 'center' }}>
                                          {getMessageStatusIcon(msg.status, msg.message_type)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            <div ref={messagesEndRef} />
                          </div>

                          {/* Message Input - WhatsApp Style */}
                          <div 
                            style={{ 
                              backgroundColor: '#F0F0F0',
                              padding: '8px',
                              flexShrink: 0,
                              borderTop: '1px solid #e0e0e0',
                              position: 'sticky',
                              bottom: 0,
                              zIndex: 10
                            }}
                          >
                            <form onSubmit={handleSendMessage}>
                              <div className="d-flex align-items-end" style={{ gap: '8px' }}>
                                <div 
                                  style={{ 
                                    flex: 1,
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '21px',
                                    padding: '9px 12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: '42px',
                                    maxHeight: '100px',
                                    overflowY: 'auto',
                                    overflowX: 'hidden'
                                  }}
                                >
                                  <textarea
                                    value={newMessage}
                                    onChange={(e) => {
                                      setNewMessage(e.target.value);
                                      // Auto resize
                                      e.target.style.height = 'auto';
                                      e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (newMessage.trim() && !sending) {
                                          const formEvent = { preventDefault: () => {} };
                                          handleSendMessage(formEvent);
                                        }
                                      }
                                    }}
                                    placeholder="Mesaj yazın..."
                                    disabled={sending}
                                    style={{
                                      border: 'none',
                                      outline: 'none',
                                      resize: 'none',
                                      flex: 1,
                                      fontSize: '15px',
                                      lineHeight: '20px',
                                      fontFamily: 'inherit',
                                      maxHeight: '84px',
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                      padding: 0,
                                      margin: 0,
                                      width: '100%'
                                    }}
                                    rows={1}
                                  />
                                </div>
                                <button
                                  type="submit"
                                  disabled={!newMessage.trim() || sending}
                                  style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    backgroundColor: activeChannel === 'whatsapp' ? '#25D366' : channels.find(c => c.key === activeChannel)?.color || '#007bff',
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: (!newMessage.trim() || sending) ? 'not-allowed' : 'pointer',
                                    opacity: (!newMessage.trim() || sending) ? 0.5 : 1,
                                    transition: 'opacity 0.2s'
                                  }}
                                >
                                  {sending ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '16px', height: '16px' }}></span>
                                  ) : (
                                    <Icon icon="solar:plain-2-bold" style={{ fontSize: '20px' }} />
                                  )}
                                </button>
                              </div>
                            </form>
                          </div>
                        </>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 text-muted" style={{ backgroundColor: '#ECE5DD' }}>
                          <div className="text-center">
                            <Icon icon="solar:chat-round-dots-bold" style={{ fontSize: '4rem', opacity: 0.3 }} />
                            <p className="mt-3" style={{ color: '#667781' }}>Bir konuşma seçin</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default MessagesPage;

